import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NewRecipientModel } from '@pg-app/client/manage-recipient/models/recipient.model';
import { RecipientService } from '@pg-app/client/manage-recipient/services/recipient.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { SendPaymentInstructionModelService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.model.service';
import { SendPaymentInstructionComponent } from '@pg-app/client/send-payment-instruction/containers/send-payment/send-payment-instruction.component';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { RecipientInfoService } from '@pg-app/client/manage-recipient/services/recipient-info.service';
import { VerifyCodeErrorEnum } from '@nttp/enum/verify-code-error-type.enum';
import { DialogTypeEnum } from '@nttp/enum/common-dialog-type.enum';
import { RegistrationStep2Service } from '@pg-app/client/services/registrationStep2.service';
import { AuthyTicketTypeEnum } from '@nttp/enum/authy-ticket-type.enum';
import { VerifyTypeSendCodeEnum } from '@nttp/enum/verify-type.enum';
import { SendPaymentTypeEnum } from '@nttp/enum/send-payment-type.enum';
import { SendPaymentModalComponent } from '@pg-app/client/client-dashboard/components/send-payment-modal/send-payment-modal.component';
import { AddRecipientFrom } from '@pg-app/client/manage-recipient/constants/add-recipient-constants';
import { AddBeneficiaryStep } from '@pg-app/client/manage-recipient/constants/add-beneficiary-constant';
import { BeneficiarySourceEnum } from '@pg-app/shared/enums/beneficiary-source.enum';
import { format } from 'date-fns';

@Component({
  selector: 'np-add-new-recipient-verify-code',
  templateUrl: './verify-code.component.html',
  styleUrls: ['./verify-code.component.scss']
})
export class AddNewRecipientVerifyCodeComponent implements OnInit {
  authyCompleted = false;
  @Input() action = AddRecipientFrom.ManageRecipient;
  @Input() remitterRequired: boolean;
  @Input() beneficiarySource: BeneficiarySourceEnum;

  @Output()
  submited = new EventEmitter<boolean>();

  addRecipientFrom = AddRecipientFrom;

  btnName = 'Continue';
  email = '';
  pinCodeError = null;
  ticketType = AuthyTicketTypeEnum.CreateRecipient;
  sendOnetouchSuccess = false;

  constructor(
    private newRecipientModel: NewRecipientModel,
    private recipientService: RecipientService,
    private toastr: ToastrService,
    public dialog: MatDialog,
    private router: Router,
    private paymentModel: SendPaymentInstructionModelService,
    private recipientInfo: RecipientInfoService,
    public registerService: RegistrationStep2Service,
  ) {}

  ngOnInit() {
    this.newRecipientModel.currentStep = AddBeneficiaryStep.Authy;
  }

  onSubmitted(event) {
    this.pinCodeError = null;
    this.recipientService.verificationPassCode(event.code, event.verificationType).subscribe(
      _result => {
        if (this.action === AddRecipientFrom.Bank) {
          this.submited.emit(true);
        } else {
          this.addRecipient();
        }
      },
      _error => {
        this.pinCodeError = VerifyCodeErrorEnum.InvalidCode;
      }
    );
  }

  addRecipient() {
    const recipientModel = {
      receipient: {
        name: this.newRecipientModel.step1.recipientName,
        countryId: this.newRecipientModel.step1.recipientCountry.id || null,
        currencyId: this.newRecipientModel.step1.currency.id,
        emailAddress: this.newRecipientModel.step1.email,
        isNotified: this.newRecipientModel.step1.notification === 'yes',
        status: 1,
        paymentMandate: this.newRecipientModel.step2.mandate,
        isPEPSanctions: false,
        address1: this.newRecipientModel.step1.recipientAddress1 || null,
        address2: this.newRecipientModel.step1.recipientAddress2 || null,
        beneficiaryType: this.newRecipientModel.step1.beneficiaryType,
        beneficiaryCity: this.newRecipientModel.step1.beneficiaryCity || null,
        beneficiaryStateProvince: this.newRecipientModel.step1.beneficiaryState || null,
        beneficiaryPostCode: this.newRecipientModel.step1.beneficiaryPostCode || null,
        beneficiaryTaxId: this.newRecipientModel.step1.beneficiaryTaxId || null,
      },
      ultimateRemitter: null,
      existingUltimateRemitterIdSelected: this.newRecipientModel.existingUltimateRemitterIdSelected,
      mainBank: {
        name: this.newRecipientModel.step2.bankName,
        accountNumber: this.newRecipientModel.step2.accountNumber || null,
        address1: this.newRecipientModel.step2.address1,
        address2: this.newRecipientModel.step2.address2 || null,
        address3: this.newRecipientModel.step2.address3 || null,
        city: this.newRecipientModel.step2.city || null,
        province: this.newRecipientModel.step2.stateProvince || null,
        postCode: this.newRecipientModel.step2.zipPostalCode || null,
        countryId: this.newRecipientModel.step1.country.id || null,
        status: 1,
        iBan: this.newRecipientModel.step2.IBAN || null,
        reference: this.newRecipientModel.step2.reference || null,
        sWiftCode: this.newRecipientModel.step2.SWIFTCode || null,
        sortCodeUK: this.newRecipientModel.step2.sortCode || null,
        bankCode: this.newRecipientModel.step2.bankCode || null,
        purposeOfPaymentId: this.newRecipientModel.step2.purposeOfPayment || null,
        bankAccountType: this.newRecipientModel.step2.bankAccountType,
        acceptSEPA: this.newRecipientModel.step2.acceptSEPA
      },
      isIntermediary: false,
      intermediaryBank: null,
      isSendOneOffPayment: this.newRecipientModel.step2.isSendOneOffPayment,
      clientId: null
    };

    if (!recipientModel.existingUltimateRemitterIdSelected && this.beneficiarySource === BeneficiarySourceEnum.payment) {
      recipientModel.ultimateRemitter = {
        Required: this.remitterRequired,
        Type: this.newRecipientModel.remitterDetail.ultimateRemitterType,
        Address: this.newRecipientModel.remitterDetail.ultimateRemitterAddress,
        DateOfBirthString: format(this.newRecipientModel.remitterDetail.ultimateRemitterDOB, 'YYYYMMDD'),
        CompanyName: this.newRecipientModel.remitterDetail.ultimateRemitterCompanyName,
        CompanyNumber: this.newRecipientModel.remitterDetail.ultimateRemitterCompanyNumber,
        FirstName: this.newRecipientModel.remitterDetail.ultimateRemitterFirstName,
        LastName: this.newRecipientModel.remitterDetail.ultimateRemitterLastName,
        City: this.newRecipientModel.remitterDetail.ultimateRemitterCity,
        PostCode: this.newRecipientModel.remitterDetail.ultimateRemitterPostCode,
        StateProvince: this.newRecipientModel.remitterDetail.ultimateRemitterState,
        CountryId: this.newRecipientModel.remitterDetail.ultimateRemitterCountryId,
      }
    }

    this.recipientService.addRecipient(recipientModel).subscribe(
      _result => {
        const ultimateRemitterId = recipientModel.existingUltimateRemitterIdSelected ?? _result.ultimateRemitterId;

        if (this.action === AddRecipientFrom.SendPayment) {
          const newRecipient = {
            name: `${this.newRecipientModel.step1.recipientName} (${this.newRecipientModel.step1.currency.code})`,
            id: _result.recipientId,
            currencyId: this.newRecipientModel.step1.currency.id,
            saveForFuture: !this.newRecipientModel.step2.isSendOneOffPayment,
            bank: {
              countryId: this.newRecipientModel.step1.country.id,
              purposeOfPaymentId: this.newRecipientModel.step2.purposeOfPayment
            },
            ultimateRemitterId: ultimateRemitterId
          };
          if (this.newRecipientModel.step2.isSendOneOffPayment) {
            this.paymentModel.newRecipient = newRecipient;
          }
          this.paymentModel.selectedRecipient = newRecipient;

          this.paymentModel.ultimateRemitterId = ultimateRemitterId;

          this.router.navigate(['/client/dashboard']);
          this.paymentModel.step =
            this.paymentModel.tradeList.length > 0
              ? this.paymentModel.sendPaymentStep.balanceTradeAvailable
              : this.paymentModel.sendPaymentStep.paymentConfirmation;
          if (this.paymentModel.manageRecipientFromPayment) {
            const component =
              this.paymentModel.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade
                ? SendPaymentInstructionComponent
                : SendPaymentModalComponent;

            this.dialog.open(component, {
              disableClose: true,
              panelClass: 'custom-dialog'
            });
          }

          this.newRecipientModel.clear();
        } else {
          if (this.recipientInfo.recipientMandateExist && recipientModel.receipient.paymentMandate) {
            this.recipientService.RemoveRecipientMandateTag(this.recipientInfo.recipientMandateExist).subscribe(res => {
              this.showDialogSuccess();
            });
          } else {
            this.showDialogSuccess();
          }
        }
      }
    );
  }

  showDialogSuccess() {
    this.dialog.open(CommonDialogComponent, {
      data: {
        title: 'New beneficiary has been registered successfully.',
        dialogType: DialogTypeEnum.Success,
        routingUrl: '/client/manage-recipient'
      },
      disableClose: true
    });
  }

  exceedInvalidCode() {
    this.dialog.open(CommonDialogComponent, {
      data: {
        title:
          'You have reached the maximum number of attempts.<br>Please contact our support via email or phone number.',
        dialogType: DialogTypeEnum.Success,
        routingUrl: '/client/manage-recipient'
      },
      disableClose: true
    });
  }

  sendPassCode(event = null) {
    this.pinCodeError = null;
    const method = event ? event.method : VerifyTypeSendCodeEnum.Onetouch;
    this.recipientService.sendPassCode(method).subscribe(
      _result => {
        if (event && event.isTryAgain) {
          this.pinCodeError = VerifyCodeErrorEnum.ResendCode;
        }
        if (!event) {
          this.sendOnetouchSuccess = true;
        }
      }
    );
  }

  changeStatus(event) {
    this.authyCompleted = event;
  }

  onetouchApproved() {
    this.authyCompleted = true;
    if (this.action === AddRecipientFrom.Bank) {
      this.submited.emit(true);
    } else {
      this.addRecipient();
    }
  }

}
